/**
 * useGeoGuard — Non-US visitor detection for MKTG-02 waitlist banner
 *
 * v1.0 - MKTG-02: Initial implementation - 2026-03-16
 *
 * Bypass check ORDER (strict — evaluated synchronously before any fetch):
 *   1. URL param ?bypass_geo=true → stash in sessionStorage, skip detection, US experience
 *   2. sessionStorage 'mylojic_geo_bypass' === 'true' → skip detection, US experience
 *   3. localStorage 'mylojic_waitlist_submitted' === 'true' → skip (already submitted)
 *   4. Fetch GET /api/visitor-country
 *   5. country !== 'US' → showWaitlistBanner = true
 *
 * Tester bypass URL: www.mylojic.com?bypass_geo=true
 */
import { useState, useEffect } from 'react';
import { portalRequestUngated } from '../services/portalRequest'; // v4.28 - G10-B: one request wrapper, carries the chosen company

export interface GeoGuardResult {
  showWaitlistBanner: boolean;
  detectedCountry: string;
}

export function useGeoGuard(): GeoGuardResult {
  const [showWaitlistBanner, setShowWaitlistBanner] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState('US');

  useEffect(() => {
    // 1. URL param bypass — stash in sessionStorage so it persists through navigation
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('bypass_geo') === 'true') {
      try { sessionStorage.setItem('mylojic_geo_bypass', 'true'); } catch (_) {}
      return; // US experience, no banner
    }

    // 2. Session bypass already set
    try {
      if (sessionStorage.getItem('mylojic_geo_bypass') === 'true') return;
    } catch (_) {}

    // 3. Already submitted waitlist — don't show again
    try {
      if (localStorage.getItem('mylojic_waitlist_submitted') === 'true') return;
    } catch (_) {}

    // 4. Fetch country from BFF (Accept-Language-based detection)
    portalRequestUngated('/api/visitor-country')
      .then((res) => res.json())
      .then((data: { country?: string }) => {
        const country = (data.country || 'US').toUpperCase();
        setDetectedCountry(country);
        // 5. Show banner only for non-US visitors
        if (country !== 'US') {
          setShowWaitlistBanner(true);
        }
      })
      .catch(() => {
        // Safe-fail: network error → assume US, no banner
      });
  }, []); // Run once on mount

  return { showWaitlistBanner, detectedCountry };
}
